/**
 * EmbedCodeGenerator - Component for generating embed code snippets
 *
 * Features:
 * - Strategy selection
 * - Theme selection (light/dark)
 * - Limit selection
 * - Preview of embed
 * - Copy to clipboard
 */
import { useState } from 'react'
import { Code, Copy, Check, Eye, Settings } from 'lucide-react'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('EmbedCodeGenerator')

const EmbedCodeGenerator = ({ isOpen, onClose }) => {
  const [strategy, setStrategy] = useState('SAT_MATH')
  const [theme, setTheme] = useState('light')
  const [limit, setLimit] = useState(10)
  const [copied, setCopied] = useState(false)
  const [showPreview, setShowPreview] = useState(true)

  // Strategy options
  const strategies = [
    { value: 'SAT_MATH', label: 'SAT Math' },
    { value: 'SAT_MATH_MINI', label: 'SAT Math Mini' },
    { value: 'SAT_READING_WRITING', label: 'SAT Reading & Writing' },
    { value: 'SAT_READING_WRITING_MINI', label: 'SAT Reading & Writing Mini' }
  ]

  // Generate embed URL
  const baseUrl = window.location.origin
  const embedUrl = `${baseUrl}/rankings/embed?strategy=${strategy}&theme=${theme}&limit=${limit}`

  // Generate embed code
  const embedCode = `<iframe
  src="${embedUrl}"
  width="400"
  height="${Math.min(350 + (limit - 10) * 25, 600)}"
  frameborder="0"
  style="border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);"
  title="${strategies.find((s) => s.value === strategy)?.label || 'SAT'} Leaderboard">
</iframe>`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopied(true)
      toast.success('Embed code copied!')
      setTimeout(() => setCopied(false), 3000)
    } catch (error) {
      logger.error('Error copying:', error)
      toast.error('Failed to copy')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Code className="w-5 h-5" />
              Embed Leaderboard
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="sr-only">Close</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Configuration
                </h3>

                {/* Strategy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Challenge Type
                  </label>
                  <select
                    value={strategy}
                    onChange={(e) => setStrategy(e.target.value)}
                    className="input w-full"
                  >
                    {strategies.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Theme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Theme
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTheme('light')}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        theme === 'light'
                          ? 'border-primary-500 bg-white'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700'
                      }`}
                    >
                      <div className="w-full h-4 bg-gray-100 rounded mb-2" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Light</span>
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        theme === 'dark'
                          ? 'border-primary-500 bg-gray-800'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-800'
                      }`}
                    >
                      <div className="w-full h-4 bg-gray-700 rounded mb-2" />
                      <span className="text-sm text-gray-300">Dark</span>
                    </button>
                  </div>
                </div>

                {/* Limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Number of entries: {limit}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>20</span>
                  </div>
                </div>

                {/* Embed Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Embed Code
                  </label>
                  <div className="relative">
                    <pre className="p-3 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-x-auto">
                      <code>{embedCode}</code>
                    </pre>
                    <button
                      onClick={handleCopy}
                      className={`absolute top-2 right-2 p-2 rounded-lg transition-colors ${
                        copied
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Preview
                  </h3>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {showPreview ? 'Hide' : 'Show'}
                  </button>
                </div>

                {showPreview && (
                  <div
                    className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                    style={{ height: Math.min(350 + (limit - 10) * 25, 600) }}
                  >
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Embed Preview"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
            <button onClick={handleCopy} className="btn-primary flex items-center gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copy Code
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmbedCodeGenerator
